import { useState, useEffect } from 'react';
import { Menu, X, Phone, Home, User, Layers, FolderOpen, Mail } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';

// Utilisation des icônes Lucide
const HomeIcon = Home;
const AboutIcon = User;
const ServicesIcon = Layers;
const ProjectsIcon = FolderOpen;
const ContactIcon = Mail;

// Mettre à jour votre tableau
const navItems = [
  { id: 'accueil', label: 'Accueil', href: '/', icon: <HomeIcon className="w-5 h-5" /> },
  { id: 'apropos', label: 'À Propos', href: '/about', icon: <AboutIcon className="w-5 h-5" /> },
  { id: 'services', label: 'Services', href: '/services', icon: <ServicesIcon className="w-5 h-5" /> },
  { id: 'projets', label: 'Projets', href: '/projects', icon: <ProjectsIcon className="w-5 h-5" /> },
  { id: 'contact', label: 'Contact', href: '/contact', icon: <ContactIcon className="w-5 h-5" /> },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('accueil');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();
  const location = useLocation();
  const CLOUDINARY_BASE = "https://res.cloudinary.com/dpk0ydobt/image/upload/q_auto,f_auto/placof/";

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Détection du scroll pour changer l'apparence
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Suivi de la souris (effet visuel décoratif)
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Fermer le menu si clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      const menuButton = document.getElementById('mobile-menu-button');
      const menuPanel = document.getElementById('mobile-menu-panel');

      if (
        isMenuOpen &&
        menuButton &&
        menuPanel &&
        !menuButton.contains(event.target) &&
        !menuPanel.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Synchroniser activeLink avec l'URL actuelle
  useEffect(() => {
    const currentPath = location.pathname;
    const matchingItem = navItems.find(item => item.href === currentPath);
    if (matchingItem) {
      setActiveLink(matchingItem.id);
    } else {
      setActiveLink('accueil'); // Fallback à accueil si aucune correspondance
    }
  }, [location.pathname]);

  const handleNavClick = (id, href) => {
    setActiveLink(id);
    setIsMenuOpen(false);
    navigate(href);
  };

  return (
    <>
      <header
        className={`fixed w-full top-0 left-0 z-50 transition-all duration-700 h-16 ${isScrolled
            ? 'bg-white/80 backdrop-blur-2xl shadow-2xl py-1'
            : 'bg-gradient-to-b from-white/95 via-white/90 to-white/85 backdrop-blur-xl shadow-lg py-1'
          } border-b border-amber-100/50`}
        style={{
          background: isScrolled
            ? `linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(251,191,36,0.05) 50%, rgba(255,255,255,0.9) 100%)`
            : `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(251,191,36,0.1) 25%, rgba(249,115,22,0.05) 75%, rgba(255,255,255,0.95) 100%)`,
        }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[-1]">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full opacity-30 animate-pulse"
              style={{
                left: `${20 + i * 15}%`,
                top: `${30 + Math.sin(i) * 20}%`,
                animationDelay: `${i * 0.5}s`,
                transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.01}px)`,
              }}
            />
          ))}
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative h-full">
          <div className="flex justify-between items-center h-full">
            <div className="flex items-center group cursor-pointer h-full">
              <Link to='/'> <img src={`${CLOUDINARY_BASE}logos/Logo Placof.png`} width={"200px"} alt="Logo Placof décor" /> </Link>
            </div>

            <nav className="hidden lg:flex items-center space-x-1 h-full">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id, item.href)}
                  className={`relative px-4 py-2 rounded-xl font-medium transition-all duration-500 group overflow-hidden h-full flex items-center ${activeLink === item.id
                      ? 'text-white shadow-lg'
                      : 'text-gray-700 hover:text-amber-600'
                    }`}
                >
                  {activeLink === item.id && (
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-xl animate-pulse">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-100 via-orange-50 to-amber-100 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-300 scale-95 group-hover:scale-100" />
                  <span className="relative z-10 flex items-center gap-2">
                    <span className="text-base">{item.icon}</span>
                    {item.label}
                  </span>
                </button>
              ))}
            </nav>

            <div className="hidden md:flex items-center h-full">
              <a
                href="tel:+21697286007"
                className="group relative flex items-center gap-2 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white px-4 py-2 rounded-xl font-bold shadow-lg hover:shadow-amber-500/25 transition-all duration-500 overflow-hidden transform hover:scale-105 hover:-rotate-1 h-full"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
                <Phone className="w-4 h-4 relative z-10 group-hover:animate-bounce" />
                <span className="relative z-10 text-sm">+216 97 286 007</span>
              </a>
            </div>

            <button
              id="mobile-menu-button"
              onClick={toggleMenu}
              className="z-50 lg:hidden relative w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 hover:from-amber-200 hover:to-orange-200 transition-all duration-300 shadow-md hover:shadow-lg group"
              aria-label={isMenuOpen ? "Fermer le menu mobile" : "Ouvrir le menu mobile"}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                {isMenuOpen ? (
                  <X className="w-6 h-6 text-gray-700" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700" />
                )}
              </div>
            </button>

          </div>
        </div>
      </header>

      {isMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-gradient-to-br from-black/20 via-amber-900/10 to-orange-900/20 backdrop-blur-md z-40 lg:hidden transition-all duration-500"
            onClick={() => setIsMenuOpen(false)}
          />

          <div
            id="mobile-menu-panel"
            className="fixed top-16 left-4 right-4 bg-white/95 backdrop-blur-2xl rounded-xl shadow-2xl z-50 lg:hidden overflow-hidden border border-amber-100/50"
          >
            <div className="h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500" />
            <div className="p-4">
              <nav className="space-y-2">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id, item.href)}
                    className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 group relative overflow-hidden ${activeLink === item.id
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md'
                        : 'text-gray-700 hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50 hover:text-amber-600'
                      }`}
                  >
                    <span className="relative z-10 flex items-center gap-3">
                      <span className="text-lg">{item.icon}</span>
                      {item.label}
                    </span>
                  </button>
                ))}
              </nav>

              <div className="mt-8 pt-6 border-t border-gradient-to-r from-amber-100 to-orange-100">
                <a
                  href="tel:+21697286007"
                  className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white px-6 py-5 rounded-2xl font-bold shadow-2xl group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <Phone className="w-5 h-5 relative z-10 group-hover:animate-bounce" />
                  <span className="relative z-10">+216 97 286 007</span>
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;
